// Loads the signed-in account's data once, keeps it current as the user edits, and shares it with
// every screen.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CategoryCatalog } from "../domain/categories.ts";
import type { AccountStatus, Rate, SavedPlace, Trip, Vehicle } from "../domain/model.ts";
import { MileageRates } from "../domain/rates.ts";
import { supabase } from "../lib/supabase.ts";
import {
  type AccountStatusRow, type CustomCategoryRow, type PlaceRow, type RateRow, type TripRow, type VehicleRow,
  customCategoryFrom, placeFrom, rateEntry, rateFrom, routeFrom, statusFrom, tripFrom, tripListColumns, vehicleFrom,
} from "./rows.ts";

export interface AccountData {
  userId: string;
  email: string | null;
  status: AccountStatus;
  /** Newest start first. */
  trips: Trip[];
  vehicles: Vehicle[];
  catalog: CategoryCatalog;
  rates: MileageRates;
  rateRows: Rate[];
  places: SavedPlace[];
  customCategoryCount: number;
  /** The newest change the phone sent: the latest `updated_at` of any row. */
  lastUpload: Date | null;
}

export type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; data: AccountData };

export interface TripEdit {
  categoryRaw: string | null;
  purpose: string | null;
  notes: string | null;
}

export type SaveResult = { ok: true } | { ok: false; message: string; subscriptionEnded: boolean };

interface AccountContextValue {
  state: LoadState;
  reload(): void;
  /** Saves category, purpose and notes through `edit_trip`, showing the change at once. */
  editTrip(tripId: string, edit: TripEdit): Promise<SaveResult>;
  /** The open trip's route, fetched on demand since the list leaves routes out. */
  loadRoute(tripId: string): Promise<[number, number, number][]>;
}

const AccountContext = createContext<AccountContextValue | null>(null);

const pageSize = 1000;

async function allRows<T>(table: string, columns: string, order: { column: string; ascending: boolean }): Promise<T[]> {
  const rows: T[] = [];
  for (;;) {
    const { data, error } = await supabase
      .from(table)
      .select(columns)
      .is("deleted_at", null)
      .order(order.column, { ascending: order.ascending })
      .order("id")
      .range(rows.length, rows.length + pageSize - 1);
    if (error) throw error;
    rows.push(...((data ?? []) as T[]));
    if (!data || data.length < pageSize) return rows;
  }
}

async function load(userId: string, email: string | null): Promise<AccountData> {
  const [statusResult, tripRows, vehicleRows, categoryRows, rateRows, placeRows] = await Promise.all([
    supabase.rpc("account_status"),
    allRows<TripRow>("trips", tripListColumns, { column: "start_date", ascending: false }),
    allRows<VehicleRow>("vehicles", "id,name,unit,created_at,updated_at", { column: "created_at", ascending: true }),
    allRows<CustomCategoryRow>("custom_categories", "id,name,system_image,color,is_deductible,is_archived,sort_order,created_at,updated_at", { column: "sort_order", ascending: true }),
    allRows<RateRow>("mileage_rates", "id,year,start_month,start_day,category,amount,unit,currency_code,rate_updated_at,updated_at", { column: "year", ascending: true }),
    allRows<PlaceRow>("saved_places", "id,name,address,latitude,longitude,kind,updated_at", { column: "name", ascending: true }),
  ]);
  if (statusResult.error) throw statusResult.error;

  const rates = rateRows.map(rateFrom);
  const updated = [...tripRows, ...vehicleRows, ...categoryRows, ...rateRows, ...placeRows]
    .map((row) => new Date(row.updated_at).getTime())
    .filter(Number.isFinite);

  return {
    userId,
    email,
    status: statusFrom((statusResult.data as AccountStatusRow[] | null)?.[0]),
    trips: tripRows.map(tripFrom),
    vehicles: vehicleRows.map(vehicleFrom),
    catalog: new CategoryCatalog(categoryRows.map(customCategoryFrom)),
    rates: new MileageRates(rates.map(rateEntry)),
    rateRows: rates,
    places: placeRows.map(placeFrom),
    customCategoryCount: categoryRows.length,
    lastUpload: updated.length > 0 ? new Date(Math.max(...updated)) : null,
  };
}

function errorMessage(error: unknown): string {
  if (error && typeof error === "object" && "message" in error && typeof error.message === "string") return error.message;
  return "Something went wrong.";
}

export function AccountProvider({ userId, email, children }: { userId: string; email: string | null; children: React.ReactNode }) {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);
  const routes = useRef(new Map<string, [number, number, number][]>());

  useEffect(() => {
    let cancelled = false;
    setState((current) => (current.kind === "loaded" ? current : { kind: "loading" }));
    load(userId, email)
      .then((data) => { if (!cancelled) setState({ kind: "loaded", data }); })
      .catch((error: unknown) => { if (!cancelled) setState({ kind: "error", message: errorMessage(error) }); });
    return () => { cancelled = true; };
  }, [userId, email, attempt]);

  const reload = useCallback(() => setAttempt((value) => value + 1), []);

  const replaceTrip = useCallback((tripId: string, update: (trip: Trip) => Trip) => {
    setState((current) => {
      if (current.kind !== "loaded") return current;
      const trips = current.data.trips.map((trip) => (trip.id === tripId ? update(trip) : trip));
      return { kind: "loaded", data: { ...current.data, trips } };
    });
  }, []);

  const editTrip = useCallback(async (tripId: string, edit: TripEdit): Promise<SaveResult> => {
    // Shown at once; the server's answer replaces it.
    replaceTrip(tripId, (trip) => ({
      ...trip,
      categoryRaw: edit.categoryRaw,
      classificationSource: edit.categoryRaw === trip.categoryRaw ? trip.classificationSource : null,
      purpose: edit.purpose,
      notes: edit.notes,
    }));
    const { data, error } = await supabase.rpc("edit_trip", {
      trip_id: tripId,
      new_category: edit.categoryRaw,
      new_purpose: edit.purpose,
      new_notes: edit.notes,
    });
    if (error) {
      const subscriptionEnded = error.code === "42501";
      if (subscriptionEnded) reload();
      return { ok: false, message: error.message, subscriptionEnded };
    }
    const saved = (data as TripRow[] | null)?.[0];
    if (saved) replaceTrip(tripId, (trip) => ({ ...tripFrom(saved), route: trip.route }));
    return { ok: true };
  }, [reload, replaceTrip]);

  const loadRoute = useCallback(async (tripId: string) => {
    const cached = routes.current.get(tripId);
    if (cached) return cached;
    const { data, error } = await supabase.from("trips").select("route").eq("id", tripId).maybeSingle();
    if (error) throw error;
    const route = routeFrom((data as { route?: unknown } | null)?.route);
    routes.current.set(tripId, route);
    return route;
  }, []);

  const value = useMemo(() => ({ state, reload, editTrip, loadRoute }), [state, reload, editTrip, loadRoute]);
  return <AccountContext value={value}>{children}</AccountContext>;
}

export function useAccount(): AccountContextValue {
  const value = useContext(AccountContext);
  if (!value) throw new Error("useAccount outside AccountProvider");
  return value;
}

/** The loaded data; only for screens shown once loading has finished. */
export function useAccountData(): AccountData {
  const { state } = useAccount();
  if (state.kind !== "loaded") throw new Error("Account data isn't loaded");
  return state.data;
}
